/**
 * Transcript panel (§2.3, screenshots/05-spec-transcript-behavior.png).
 *
 * React renders the turns and segments (re-renders only when segments are appended/patched).
 * Everything that follows playback is transient:
 *   - active segment + speaking word  → data attributes toggled via useStoreEffect
 *   - auto-scroll to 1/3 from top     → scrollTo inside the same effect
 *   - pause on user scroll/selection  → followMode in the store (only <SyncChip> and the pill re-render)
 */
import { memo, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";
import { ArrowDown, ChevronDown, ChevronUp, Gavel, PanelRightClose, Play, Search, StickyNote, X } from "lucide-react";
import { meetingStore, useMeeting } from "../../stores/meetingStore";
import { notesStore, useNotes, type Draft } from "../../stores/notesStore";
import { followDraft, knownPeople } from "../shared/FollowSelection";
import { followFromSelection } from "../../lib/followText";
import { SelectionToolbar } from "./SelectionToolbar";
import { NoteComposer } from "./NoteComposer";
import { ShareClipCard, clipFromDraft, type Clip } from "./ShareClip";
import { useStoreEffect } from "../../hooks/useStoreEffect";
import { prefersReducedMotion, recipes } from "../../motion";
import { clock } from "../../lib/format";
import { seekTo } from "./vimeoClock";
import { Face } from "../shared/Face";
import { TranscriptNotReady } from "../shared/States";
import type { Note, Person, TranscriptSegment } from "../../types";
import { seatLabel } from "../../app/site";
import { allSegments } from "../../realtime/bootstrap";
import { track } from "../../lib/analytics";
import { findMatches, matchFrom, transcriptSearch, useTranscriptSearch, type Match } from "./transcriptSearch";

const EMPTY: Note[] = [];

/** The segment's text with saved highlights (spec 05 card 4). Offsets are into .tx-text's text. */
function highlighted(seg: TranscriptSegment, notes: Note[]): ReactNode {
  const hl = (from: number, to: number) => notes.find((n) => n.range.startChar < to && n.range.endChar > from)?.color;
  if (seg.words) {
    let off = 0;
    return seg.words.map((w, i) => {
      const from = off;
      off += w.w.length + 1;
      return <span key={i} data-w={i} data-hl={hl(from, from + w.w.length)}>{w.w}{" "}</span>;
    });
  }
  if (!notes.length) return seg.text;
  const cuts = new Set([0, seg.text.length]);
  for (const n of notes) { cuts.add(Math.max(0, n.range.startChar)); cuts.add(Math.min(seg.text.length, n.range.endChar)); }
  const at = [...cuts].sort((a, b) => a - b);
  return at.slice(0, -1).map((from, k) => {
    const to = at[k + 1], piece = seg.text.slice(from, to), color = hl(from, to);
    return color ? <mark key={from} data-hl={color}>{piece}</mark> : piece;
  });
}

const Segment = memo(function Segment({ seg, index, canShare }: { seg: TranscriptSegment; index: number; canShare: boolean }) {
  const notes = useNotes((s) => s.bySegment[seg.id] ?? EMPTY);
  const composer = useNotes((s) => (s.composer?.segmentId === seg.id ? s.composer : null));
  const hasNote = notes.some((n) => n.text);
  const editing = composer?.noteId ? notes.find((n) => n.id === composer.noteId) ?? null : null;
  return (
    <div className="tx-seg" data-seg={seg.id} data-i={index} data-state="upcoming">
      <span className="tx-time">
        {/* The time jumps the video to this line. */}
        <button type="button" className="tx-time__btn" onClick={() => seekTo(seg.start)} aria-label={`Play from ${clock(seg.start)}`}>
          <i className="live-dot" />{clock(seg.start)}
        </button>
        {hasNote && <StickyNote className="note-marker" size={12} aria-label="Has a note" />}
      </span>
      <div className="tx-col">
        <p className="tx-text">{highlighted(seg, notes)}</p>
        {composer && <NoteComposer t={seg.start} initial={editing} canShare={canShare} />}
      </div>
    </div>
  );
});

const Turn = memo(function Turn({ person, segments, first, canShare }: { person: Person | null; segments: TranscriptSegment[]; first: number; canShare: boolean }) {
  return (
    <div className="tx-turn">
      <div className="tx-speaker">
        {person ? (
          <>
            <Face name={person.name} photoUrl={person.photoUrl} size={22} />
            <span className="tx-speaker-name">{person.name}</span>
            {person.ward != null && <span className="tx-speaker-ward">{seatLabel(person.ward)}</span>}
          </>
        ) : (
          <>
            <span className="tx-officer" aria-hidden><Gavel size={11} /></span>
            <span className="tx-speaker-name">Presiding officer</span>
          </>
        )}
      </div>
      {segments.map((seg, k) => <Segment key={seg.id} seg={seg} index={first + k} canShare={canShare} />)}
    </div>
  );
});

function SyncChip() {
  const mode = useMeeting((s) => s.followMode);
  const label = mode === "following" ? "Following video" : mode === "paused" ? "Auto-scroll paused" : "Paused while you annotate";
  return (
    <span className="sync-chip" data-mode={mode} role="status">
      <i className="dot" />{label}
    </span>
  );
}

/**
 * The pill that resumes auto-scroll at the video's current moment. Design copy "Back to live · {time}" is for a meeting
 * in session; a recorded meeting never says Live (brief: "A recorded meeting doesn't say Live"), so there it reads
 * "Back to video · {time}". Hidden until the video has a current line to go back to (a recorded meeting not yet played).
 */
function BackToLive({ onClick }: { onClick: () => void }) {
  const mode = useMeeting((s) => s.followMode);
  const live = useMeeting((s) => s.meeting?.status === "live");
  const timeRef = useRef<HTMLSpanElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  useStoreEffect(meetingStore, (s) => Math.floor(s.currentTime), (t) => {
    if (timeRef.current) timeRef.current.textContent = clock(t);
  });
  // activeSegmentId is high-frequency: written to the DOM, never React state (§0.6).
  useStoreEffect(meetingStore, (s) => s.activeSegmentId != null, (has) => { btnRef.current?.toggleAttribute("data-idle", !has); });
  return (
    <button ref={btnRef} className="back-to-live" onClick={onClick} hidden={mode === "following"} data-idle="">
      <ArrowDown size={13} />
      {live ? "Back to live" : "Back to video"} · <span ref={timeRef} />
    </button>
  );
}

// ---------- search (transcriptSearch.ts) ----------

/** The DOM range of characters [from, to) of an element's text (its text nodes, in order). */
function rangeIn(el: Element, from: number, to: number): Range | null {
  const walk = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  const range = document.createRange();
  let pos = 0, started = false;
  for (let n = walk.nextNode(); n; n = walk.nextNode()) {
    const len = n.textContent?.length ?? 0;
    if (!started && from < pos + len) { range.setStart(n, from - pos); started = true; }
    if (started && to <= pos + len) { range.setEnd(n, to - pos); return range; }
    pos += len;
  }
  return null;
}

type HighlightRegistry = { set: (name: string, h: unknown) => void; delete: (name: string) => void };
const highlights = () => (globalThis as { CSS?: { highlights?: HighlightRegistry } }).CSS?.highlights;
const HighlightCtor = () => (globalThis as { Highlight?: new (...r: Range[]) => { add: (r: Range) => void } }).Highlight;

/** Mark the matches (and the current one) in the transcript's text; without the Highlight API, mark their lines. */
function paintMatches(body: HTMLElement | null, matches: Match[], current: number) {
  if (!body) return;
  body.querySelectorAll(".tx-seg[data-match]").forEach((el) => el.removeAttribute("data-match"));
  const reg = highlights(), H = HighlightCtor();
  const all = H ? new H() : null, cur = H ? new H() : null;
  matches.forEach((m, k) => {
    const text = body.querySelector(`[data-seg="${CSS.escape(m.segId)}"] .tx-text`);
    if (!text) return;
    text.closest(".tx-seg")!.setAttribute("data-match", k === current ? "current" : "");
    const r = all && rangeIn(text, m.from, m.to);
    if (r) (k === current ? cur! : all!).add(r);
  });
  if (reg && all && cur) { reg.set("tx-search", all); reg.set("tx-search-current", cur); }
}
const unpaint = (body: HTMLElement | null) => {
  highlights()?.delete("tx-search"); highlights()?.delete("tx-search-current");
  body?.querySelectorAll(".tx-seg[data-match]").forEach((el) => el.removeAttribute("data-match"));
};

/** The matches of the open search over the whole transcript, the current one, and moving between them. */
function useTranscriptMatches(bodyRef: RefObject<HTMLDivElement | null>) {
  const { open, query, startAt } = useTranscriptSearch();
  const segments = useMeeting((s) => s.segments);
  const meetingId = useMeeting((s) => s.meeting?.id ?? null);
  const phrase = useDeferredValue(open ? query : "");
  const searching = phrase.trim().length >= 2;
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(false);
  // A long meeting's later pages first, so the search covers the whole transcript.
  useEffect(() => {
    if (!searching || !meetingId) return;
    let dead = false;
    setLoading(true);
    void allSegments(meetingId).finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  }, [searching, meetingId]);
  const matches = useMemo(() => (searching ? findMatches(segments, phrase) : []), [segments, phrase, searching]);
  // GA4 transcript_search: once the reader stops typing, whether the phrase was found (never the phrase itself).
  const searched = useRef("");
  const found = matches.length > 0;
  useEffect(() => {
    if (!searching || loading || !meetingId || searched.current === phrase.trim()) return;
    const t = setTimeout(() => { searched.current = phrase.trim(); track("transcript_search", { meeting_id: meetingId, found }); }, 1500);
    return () => clearTimeout(t);
  }, [searching, loading, meetingId, phrase, found]);
  useEffect(() => { setCurrent(matchFrom(matches, startAt)); }, [phrase, startAt, matches.length]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const body = bodyRef.current;
    if (!matches.length) { unpaint(body); return; }
    paintMatches(body, matches, current);
    const el = body?.querySelector<HTMLElement>(`[data-seg="${CSS.escape(matches[current].segId)}"]`);
    if (body && el) {
      if (meetingStore.getState().followMode === "following") meetingStore.getState().setFollowMode("paused");
      body.scrollTo({ top: el.offsetTop - body.clientHeight / 3, behavior: prefersReducedMotion() ? "auto" : "smooth" });
    }
  }, [matches, current, bodyRef]);
  useEffect(() => () => unpaint(bodyRef.current), [bodyRef]);
  const step = (d: number) => matches.length && setCurrent((c) => (c + d + matches.length) % matches.length);
  return { open, query, matches, current, setCurrent, step, loading: loading && searching };
}

/** The search row: the phrase, "3 of 12", previous / next (Enter, Shift+Enter), play from the match, close (Esc). */
function SearchRow({ search, onClose }: { search: ReturnType<typeof useTranscriptMatches>; onClose: () => void }) {
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => { input.current?.focus(); }, []);
  const { query, matches, current, step, loading } = search;
  const m = matches[current];
  const count = query.trim().length < 2 ? "" : loading && !matches.length ? "Searching…" : matches.length ? `${current + 1} of ${matches.length}` : "No matches";
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") { e.preventDefault(); step(e.shiftKey ? -1 : 1); }
    else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onClose(); }
  };
  return (
    <div className="tx-search" role="search" onKeyDown={onKey}>
      <Search size={14} aria-hidden />
      <input ref={input} type="search" aria-label="Search the transcript" placeholder="Search the transcript" value={query}
        onChange={(e) => transcriptSearch.setQuery(e.target.value)} />
      <span className="tx-search__count" aria-live="polite">{count}</span>
      <button type="button" className="icon-btn" aria-label="Previous match" disabled={!matches.length} onClick={() => step(-1)}><ChevronUp size={15} /></button>
      <button type="button" className="icon-btn" aria-label="Next match" disabled={!matches.length} onClick={() => step(1)}><ChevronDown size={15} /></button>
      <button type="button" className="icon-btn" aria-label={m ? `Play from ${clock(m.t)}` : "Play from this match"} disabled={!m} onClick={() => m && seekTo(m.t)}><Play size={14} /></button>
      <button type="button" className="icon-btn" aria-label="Close search" onClick={onClose}><X size={15} /></button>
    </div>
  );
}

/** Where a text selection inside one segment starts and ends, as offsets into its .tx-text. */
function selectionDraft(body: HTMLElement, people: Record<string, Person>): Draft | null {
  const sel = window.getSelection();
  if (!sel || sel.isCollapsed || !sel.rangeCount) return null;
  const range = sel.getRangeAt(0);
  const segEl = (range.startContainer.nodeType === 1 ? (range.startContainer as Element) : range.startContainer.parentElement)?.closest<HTMLElement>(".tx-seg");
  const textEl = segEl?.querySelector<HTMLElement>(".tx-text");
  if (!segEl || !textEl || !body.contains(segEl) || !textEl.contains(range.startContainer)) return null;
  const pre = document.createRange();
  pre.setStart(textEl, 0);
  pre.setEnd(range.startContainer, range.startOffset);
  const full = textEl.textContent ?? "";
  const start = pre.toString().length;
  // A selection running into the next segment is clamped to this one.
  const end = textEl.contains(range.endContainer) ? start + range.toString().length : full.length;
  const quote = full.slice(start, end).trim();
  if (!quote) return null;
  const seg = meetingStore.getState().segments.find((x) => x.id === segEl.dataset.seg);
  if (!seg) return null;
  const p = seg.speakerId ? people[seg.speakerId] : null;
  return { segmentId: seg.id, startChar: start, endChar: end, t: seg.start, quote, speaker: p ? `${p.name}${p.ward != null ? `, ${seatLabel(p.ward)}` : ""}` : null };
}

export function TranscriptPanel({ onHide, canShare }: { onHide: () => void; canShare: boolean }) {
  const segments = useMeeting((s) => s.segments);
  const people = useMeeting((s) => s.people);
  const bodyRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const programmaticScroll = useRef(false);
  const [selection, setSelection] = useState<{ draft: Draft; x: number; y: number; bottom: number } | null>(null);
  const [share, setShare] = useState<{ clip: Clip; top: number } | null>(null);
  const search = useTranscriptMatches(bodyRef);
  const searchBtn = useRef<HTMLButtonElement>(null);
  const closeSearch = () => { transcriptSearch.close(); requestAnimationFrame(() => searchBtn.current?.focus()); };
  // A click on a line with a match plays from that match (the moment the words are said).
  const onMatchClick = (e: React.MouseEvent) => {
    if (!search.matches.length || !window.getSelection()?.isCollapsed) return;
    const seg = (e.target as HTMLElement).closest<HTMLElement>(".tx-seg[data-match]");
    if (!seg || (e.target as HTMLElement).closest("button, a, textarea, input")) return;
    const inSeg = (i: number) => search.matches[i]?.segId === seg.dataset.seg;
    const k = inSeg(search.current) ? search.current : search.matches.findIndex((m) => m.segId === seg.dataset.seg);
    if (k < 0) return;
    search.setCurrent(k);
    seekTo(search.matches[k].t);
  };

  // Consecutive segments by the same speaker form one turn.
  const turns = useMemo(() => {
    const out: { key: string; speakerId: string | null; first: number; segments: TranscriptSegment[] }[] = [];
    segments.forEach((seg, i) => {
      const sid = seg.speakerId ?? null;
      const last = out[out.length - 1];
      if (last && last.speakerId === sid) last.segments.push(seg);
      else out.push({ key: seg.id, speakerId: sid, first: i, segments: [seg] });
    });
    return out;
  }, [segments]);

  const scrollToActive = useCallback((id: string | null, smooth = true) => {
    const body = bodyRef.current;
    if (!body || !id) return;
    const el = body.querySelector<HTMLElement>(`[data-seg="${CSS.escape(id)}"]`);
    if (!el) return;
    programmaticScroll.current = true;
    body.scrollTo({
      top: el.offsetTop - body.clientHeight * recipes.transcriptScroll.anchor,
      behavior: smooth && !prefersReducedMotion() ? "smooth" : "auto",
    });
    window.setTimeout(() => (programmaticScroll.current = false), recipes.transcriptScroll.duration + 120);
  }, []);

  // Active segment: past/active/upcoming states + auto-scroll. No re-render.
  useStoreEffect(meetingStore, (s) => s.activeSegmentId, (id) => {
    const body = bodyRef.current;
    if (!body) return;
    const active = id ? body.querySelector<HTMLElement>(`[data-seg="${CSS.escape(id)}"]`) : null;
    const at = active ? Number(active.dataset.i) : -1;
    body.querySelectorAll<HTMLElement>(".tx-seg").forEach((el) => {
      const k = Number(el.dataset.i);
      const state = k < at ? "past" : k === at ? "active" : "upcoming";
      if (el.dataset.state !== state) el.dataset.state = state;
    });
    if (meetingStore.getState().followMode === "following") scrollToActive(id);
  });

  // The line being spoken (§2.3): words spoken so far turn black; those on the current visual line
  // get the speaking band, which sweeps word by word. No re-render.
  useStoreEffect(meetingStore, (s) => [s.activeSegmentId, s.activeWordIndex] as const, ([id, w]) => {
    const seg = id ? bodyRef.current?.querySelector(`[data-seg="${CSS.escape(id)}"]`) : null;
    if (!seg) return;
    const words = seg.querySelectorAll<HTMLElement>("[data-w]");
    const top = w >= 0 ? words[w]?.offsetTop : null;
    words.forEach((el, k) => {
      el.toggleAttribute("data-spoken", k <= w);
      el.toggleAttribute("data-line", k <= w && el.offsetTop === top);
    });
  }, { equalityFn: (a, b) => a[0] === b[0] && a[1] === b[1] });

  // Any user-initiated scroll pauses following. Programmatic scrolls are ignored.
  const onUserScroll = () => {
    if (programmaticScroll.current) return;
    if (meetingStore.getState().followMode === "following") meetingStore.getState().setFollowMode("paused");
  };

  const onPointerUp = () => {
    const body = bodyRef.current, panel = panelRef.current;
    if (!body || !panel) return;
    const draft = selectionDraft(body, people);
    if (!draft) return;
    meetingStore.getState().setFollowMode("annotating");
    const r = window.getSelection()!.getRangeAt(0).getBoundingClientRect(), p = panel.getBoundingClientRect();
    setSelection({ draft, x: r.left + r.width / 2 - p.left, y: r.top - p.top - 8, bottom: r.bottom - p.top + 8 });
  };

  // Keyboard selection (Shift+arrows with caret browsing, ⌘A/Ctrl+A in the transcript) opens the same toolbar and moves
  // focus to it, so highlight / note / share / follow work without a mouse (brief §7).
  const onSelectKey = (e: React.KeyboardEvent) => {
    if (!(e.key === "Shift" || e.shiftKey || ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "a"))) return;
    if (window.getSelection()?.isCollapsed) return;
    onPointerUp();
    requestAnimationFrame(() => panelRef.current?.querySelector<HTMLElement>(".sel-toolbar button")?.focus());
  };

  // Reopened from the video, or a note was opened: jump to the live moment at once.
  useEffect(() => {
    const live = () => requestAnimationFrame(() => scrollToActive(meetingStore.getState().activeSegmentId, false));
    addEventListener("transcript:live", live);
    return () => removeEventListener("transcript:live", live);
  }, [scrollToActive]);

  // The toolbar goes away when the selection does (click elsewhere, Escape, scrolling the transcript).
  useEffect(() => {
    if (!selection) return;
    const close = () => setSelection(null);
    const onSel = () => { if (window.getSelection()?.isCollapsed) close(); };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && close();
    document.addEventListener("selectionchange", onSel);
    addEventListener("keydown", onKey);
    bodyRef.current?.addEventListener("scroll", close, { once: true });
    return () => { document.removeEventListener("selectionchange", onSel); removeEventListener("keydown", onKey); };
  }, [selection]);

  const done = () => { window.getSelection()?.removeAllRanges(); setSelection(null); };
  const onColor = (c: Note["color"]) => { if (selection) void notesStore.getState().highlight(selection.draft, c); done(); };
  const onAddNote = () => { if (selection) notesStore.getState().openComposer(selection.draft); done(); };
  const onShareClip = () => {
    if (!selection) return;
    const seg = meetingStore.getState().segments.find((x) => x.id === selection.draft.segmentId);
    if (seg) setShare({ clip: clipFromDraft(seg, selection.draft, people), top: selection.bottom });
    done();
  };
  // Follow from anywhere (spec 11 card 3): a name follows the person, a record number the legislation,
  // anything else the phrase as a topic.
  const onFollow = async () => {
    if (!selection) return;
    const draft = followFromSelection(selection.draft.quote, knownPeople());
    done();
    if (draft) await followDraft(draft);
  };


  const closeShare = useCallback(() => {
    setShare(null);
    if (meetingStore.getState().followMode === "annotating") meetingStore.getState().setFollowMode("paused");
  }, []);

  const backToLive = () => {
    meetingStore.getState().setFollowMode("following");
    scrollToActive(meetingStore.getState().activeSegmentId);
  };

  return (
    <aside className="tx-panel" aria-label="Transcript" ref={panelRef}>
      <header className="tx-head">
        <div className="tx-title-group">
          <h2 className="tx-title">Transcript</h2>
          <SyncChip />
        </div>
        <div className="tx-tools">
          <button ref={searchBtn} className="icon-btn" aria-label="Search transcript" aria-pressed={search.open} aria-expanded={search.open}
            onClick={() => (search.open ? closeSearch() : transcriptSearch.open())}><Search size={16} /></button>
          <button className="icon-btn" aria-label="Hide transcript" onClick={onHide}><PanelRightClose size={16} /></button>
        </div>
      </header>
      {search.open && <SearchRow search={search} onClose={closeSearch} />}
      <div
        ref={bodyRef}
        className="tx-body"
        onWheel={onUserScroll}
        onTouchMove={onUserScroll}
        // Grabbing the scrollbar presses the scroller itself (not a line): pause following, like the wheel does.
        onPointerDown={(e) => { if (e.target === e.currentTarget) onUserScroll(); }}
        onKeyDown={(e) => ["PageUp", "PageDown", "ArrowUp", "ArrowDown", "Home", "End"].includes(e.key) && onUserScroll()}
        onPointerUp={onPointerUp}
        onClick={onMatchClick}
        onKeyUp={onSelectKey}
        tabIndex={0}
      >
        {turns.map((t) => <Turn key={t.key} person={t.speakerId ? people[t.speakerId] ?? null : null} segments={t.segments} first={t.first} canShare={canShare} />)}
        {segments.length === 0 && <TranscriptNotReady /> /* screenshots/54 state 7: moments fill in when it lands */}
      </div>
      <div className="tx-fade" />
      {selection && <SelectionToolbar x={selection.x} y={selection.y} onColor={onColor} onAddNote={onAddNote} onShareClip={onShareClip} onFollow={() => void onFollow()} />}
      {share && <ShareClipCard clip={share.clip} top={share.top} onClose={closeShare} />}
      <BackToLive onClick={backToLive} />
    </aside>
  );
}
